import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';

// Theme types
export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

// Color palette based on existing Tailwind config
export interface ThemeColors {
  // Background colors
  background: {
    primary: string;
    secondary: string;
    tertiary: string;
    elevated: string;
  };
  // Text colors
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    inverse: string;
  };
  // Border colors
  border: {
    primary: string;
    secondary: string;
    focus: string;
  };
  // Interactive colors
  interactive: {
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryHover: string;
    disabled: string;
  };
  // Status colors
  status: {
    success: string;
    warning: string;
    error: string;
    info: string;
  };
  // Risk colors
  risk: {
    low: string;
    medium: string;
    high: string;
    critical: string;
  };
}

// Theme definitions
const lightTheme: ThemeColors = {
  background: {
    primary: '#ffffff',
    secondary: '#f8fafc',
    tertiary: '#f1f5f9',
    elevated: '#ffffff',
  },
  text: {
    primary: '#0f172a',
    secondary: '#334155',
    tertiary: '#64748b',
    inverse: '#ffffff',
  },
  border: {
    primary: '#e2e8f0',
    secondary: '#cbd5e1',
    focus: '#3b82f6',
  },
  interactive: {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#14b8a6',
    secondaryHover: '#0d9488',
    disabled: '#94a3b8',
  },
  status: {
    success: '#059669',
    warning: '#d97706',
    error: '#dc2626',
    info: '#0284c7',
  },
  risk: {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
    critical: '#991b1b',
  },
};

// Midnight Aurora - A unique dark theme with deep violet backgrounds
// and aurora-inspired accent colors (teal, magenta, violet)
const darkTheme: ThemeColors = {
  background: {
    primary: '#0a0a12',     // Deep space black with violet undertone
    secondary: '#12121f',   // Slightly elevated midnight
    tertiary: '#1a1a2e',    // Twilight purple-gray
    elevated: '#252538',    // Elevated surface with purple tint
  },
  text: {
    primary: '#f0f0ff',     // Soft lavender white
    secondary: '#c4c4e0',   // Muted lavender
    tertiary: '#8888a8',    // Dimmed violet-gray
    inverse: '#0a0a12',     // Inverse for buttons
  },
  border: {
    primary: '#2d2d44',     // Subtle violet border
    secondary: '#3d3d5c',   // Lighter violet border
    focus: '#a855f7',       // Vibrant purple focus ring
  },
  interactive: {
    primary: '#8b5cf6',     // Aurora violet
    primaryHover: '#a78bfa', // Lighter violet on hover
    secondary: '#06b6d4',   // Aurora cyan/teal
    secondaryHover: '#22d3ee', // Brighter cyan on hover
    disabled: '#4a4a6a',    // Muted purple-gray
  },
  status: {
    success: '#34d399',     // Bright emerald (more vivid for dark bg)
    warning: '#fbbf24',     // Bright amber
    error: '#f87171',       // Bright red-pink
    info: '#38bdf8',        // Bright sky blue
  },
  risk: {
    low: '#34d399',         // Bright emerald
    medium: '#fbbf24',      // Bright amber
    high: '#fb7185',        // Rose-red
    critical: '#ef4444',    // Bright red
  },
};

// Theme context interface
interface ThemeContextType {
  // Current theme state
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  colors: ThemeColors;
  
  // Theme actions
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  
  // System preference detection
  systemPreference: ResolvedTheme;
  
  // Utilities
  isDark: boolean;
  isLight: boolean;
  isSystemMode: boolean;
  
  // Backward compatibility
  theme: ResolvedTheme;
}

// Local storage key
const THEME_STORAGE_KEY = 'cybersec-risk-theme';

// System preference detection
const getSystemPreference = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'dark';
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
  } catch {
    return 'dark'; // Default for test environments
  }
};

// Get stored theme preference
const getStoredTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as ThemeMode;
    }
  } catch (error) {
    console.warn('Failed to read theme preference from localStorage:', error);
  }
  return 'system';
};

// Store theme preference
const storeTheme = (mode: ThemeMode): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch (error) {
    console.warn('Failed to store theme preference:', error);
  }
};

// Resolve theme mode to actual theme
const resolveTheme = (mode: ThemeMode, systemPreference: ResolvedTheme): ResolvedTheme => {
  if (mode === 'system') return systemPreference;
  return mode;
};

// Create context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme provider props
interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeMode;
}

// Theme provider component with smooth transitions
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  defaultTheme = 'system'
}) => {
  // System preference state
  const [systemPreference, setSystemPreference] = useState<ResolvedTheme>(() => getSystemPreference());
  
  // Theme mode state with migration from legacy storage
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = getStoredTheme();
    return stored || defaultTheme;
  });
  
  // Resolved theme
  const resolvedTheme = resolveTheme(mode, systemPreference);
  
  // Theme colors
  const colors = resolvedTheme === 'dark' ? darkTheme : lightTheme;
  
  // Derived state
  const isDark = resolvedTheme === 'dark';
  const isLight = resolvedTheme === 'light';
  const isSystemMode = mode === 'system';
  
  // Set theme function
  const setTheme = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    storeTheme(newMode);
  }, []);

  // Toggle theme function
  const toggleTheme = useCallback(() => {
    const newMode = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(newMode);
  }, [resolvedTheme, setTheme]);
  
  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e: MediaQueryListEvent) => {
        setSystemPreference(e.matches ? 'dark' : 'light');
      };
      
      // Add listener with proper checks
      if (mediaQuery && mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
      } else if (mediaQuery && mediaQuery.addListener) {
        // Fallback for older browsers
        mediaQuery.addListener(handleChange);
      }
      
      return () => {
        if (mediaQuery && mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleChange);
        } else if (mediaQuery && mediaQuery.removeListener) {
          mediaQuery.removeListener(handleChange);
        }
      };
    } catch {
      // Ignore errors in test environments
    }
  }, []);
  
  // Apply theme classes and CSS custom properties
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const root = window.document.documentElement;
    
    // Add transition for smooth theme switching (but only after initial load)
    const enableTransitions = () => {
      root.style.setProperty('--theme-transition', 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease');
    };
    
    // Disable transitions initially to prevent flash
    root.style.setProperty('--theme-transition', 'none');
    
    // Remove existing theme classes
    root.classList.remove('light', 'dark');
    
    // Add current theme class
    root.classList.add(resolvedTheme);
    
    // Set data attribute for CSS targeting
    root.setAttribute('data-theme', resolvedTheme);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', colors.background.primary);
    }
    
    // Set CSS custom properties for dynamic theming
    const rootStyle = root.style;
    
    // Background colors
    rootStyle.setProperty('--theme-bg-primary', colors.background.primary);
    rootStyle.setProperty('--theme-bg-secondary', colors.background.secondary);
    rootStyle.setProperty('--theme-bg-tertiary', colors.background.tertiary);
    rootStyle.setProperty('--theme-bg-elevated', colors.background.elevated);
    
    // Text colors
    rootStyle.setProperty('--theme-text-primary', colors.text.primary);
    rootStyle.setProperty('--theme-text-secondary', colors.text.secondary);
    rootStyle.setProperty('--theme-text-tertiary', colors.text.tertiary);
    rootStyle.setProperty('--theme-text-inverse', colors.text.inverse);
    
    // Border colors
    rootStyle.setProperty('--theme-border-primary', colors.border.primary);
    rootStyle.setProperty('--theme-border-secondary', colors.border.secondary);
    rootStyle.setProperty('--theme-border-focus', colors.border.focus);
    
    // Interactive colors
    rootStyle.setProperty('--theme-interactive-primary', colors.interactive.primary);
    rootStyle.setProperty('--theme-interactive-primary-hover', colors.interactive.primaryHover);
    rootStyle.setProperty('--theme-interactive-secondary', colors.interactive.secondary);
    rootStyle.setProperty('--theme-interactive-secondary-hover', colors.interactive.secondaryHover);
    rootStyle.setProperty('--theme-interactive-disabled', colors.interactive.disabled);
    
    // Status colors
    rootStyle.setProperty('--theme-status-success', colors.status.success);
    rootStyle.setProperty('--theme-status-warning', colors.status.warning);
    rootStyle.setProperty('--theme-status-error', colors.status.error);
    rootStyle.setProperty('--theme-status-info', colors.status.info);
    
    // Risk colors
    rootStyle.setProperty('--theme-risk-low', colors.risk.low);
    rootStyle.setProperty('--theme-risk-medium', colors.risk.medium);
    rootStyle.setProperty('--theme-risk-high', colors.risk.high);
    rootStyle.setProperty('--theme-risk-critical', colors.risk.critical);
    
    // Enable transitions after a brief delay to prevent flash
    const timeoutId = setTimeout(enableTransitions, 100);
    
    return () => clearTimeout(timeoutId);
  }, [resolvedTheme, colors]);
  
  const value = useMemo<ThemeContextType>(() => ({
    mode,
    resolvedTheme,
    colors,
    setTheme,
    toggleTheme,
    systemPreference,
    isDark,
    isLight,
    isSystemMode,
    theme: resolvedTheme, // Backward compatibility
  }), [mode, resolvedTheme, colors, setTheme, toggleTheme, systemPreference, isDark, isLight, isSystemMode]);
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Theme utility functions
export const themeUtils = {
  // Get theme-aware class names
  getThemeClass: (lightClass: string, darkClass: string, currentTheme?: ResolvedTheme) => {
    if (currentTheme) {
      return currentTheme === 'dark' ? darkClass : lightClass;
    }
    // Fallback to CSS class-based theming
    return `${lightClass} dark:${darkClass}`;
  },
  
  // Get CSS custom property
  getCSSVar: (property: string) => `var(--theme-${property})`,
  
  // Generate theme-aware styles
  createThemeStyles: (styles: Record<string, { light: string; dark: string }>) => {
    const result: Record<string, string> = {};
    Object.entries(styles).forEach(([key, value]) => {
      result[key] = `${value.light} dark:${value.dark}`;
    });
    return result;
  },
};

// Theme-aware component helper (backward compatibility)
export const withTheme = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { resolvedTheme } = useTheme();
    return <Component {...props} data-theme={resolvedTheme} />;
  };
};

// Export theme colors for direct access
export { lightTheme, darkTheme };